import * as Sentry from "@sentry/react";
import { DayOfWeekValue } from "components/uiElements/inputs/DayOfWeekSelector";
import APIRequest from "system/APIRequest";

export type AlertSensitivity = "high" | "medium" | "low" | "none";

export interface QueryMonitorJSON {
  _account: {
    id: string;
    name: string;
    active: boolean;
  };
  _deletable: boolean;
  _deleted: boolean;
  _modifiable: boolean;
  _owned_by_you: boolean;
  _user: {
    id: string;
    email: string;
    name: string;
    active: boolean;
  };
  account_id: string;
  alert_sensitivity: string;
  created_at: string;
  deleted_at: string | null;
  description: string;
  digest_weekdays: Array<{id: string, label: string}>;
  id: string;
  last_alert: string | null;
  last_alert_check: string;
  last_digest: string | null;
  query: Object;
  title: string;
  updated_at: string;
  user_id: string;
}

export class QueryMonitor {
  static async create(props: {
    alert_sensitivity: AlertSensitivity, 
    description: string, 
    digest_weekdays: Array<DayOfWeekValue>, 
    query: Object, 
    title: string
  }): Promise<string> {
    const response = await APIRequest("obj/query-monitor", {}, { body: JSON.stringify(props), method: "POST" });
    return response.id as string;
  }

  static async update(queryMonitorId: string, props: {
    alert_sensitivity: AlertSensitivity, 
    description: string, 
    digest_weekdays: Array<DayOfWeekValue>, 
    query: Object, 
    title: string
  }): Promise<boolean> {
    const response = await APIRequest(`obj/query-monitor/${queryMonitorId}`, {}, { body: JSON.stringify(props), method: "PUT" });
    return response.success;
  }

  static async delete(queryMonitorId: string): Promise<boolean> {
    const response = await APIRequest(`obj/query-monitor/${queryMonitorId}`, {}, { method: "DELETE" });
    return response.success;
  }

  readonly alert_sensitivity: AlertSensitivity = "medium";
  readonly created_at: string = "";
  readonly description: string = "";
  readonly digest_weekdays: Array<{id: DayOfWeekValue, label: string}> = [];
  readonly id: string = "";
  readonly last_alert: string | null = null;
  readonly last_alert_check: string | null = null;
  readonly last_digest: string | null = null;
  readonly query: Object | null = null;
  readonly title: string = "";
  readonly updated_at: string = "";

  constructor(queryMonitorJSON?: QueryMonitorJSON) {
    if (!queryMonitorJSON) {
      return;
    }

    try {
      this.alert_sensitivity = queryMonitorJSON.alert_sensitivity as AlertSensitivity;
      this.created_at = queryMonitorJSON.created_at;
      this.description = queryMonitorJSON.description;
      this.digest_weekdays = queryMonitorJSON.digest_weekdays as Array<{id: DayOfWeekValue, label: string}> ?? [];
      this.id = queryMonitorJSON.id;
      this.last_alert = queryMonitorJSON.last_alert;
      this.last_alert_check = queryMonitorJSON.last_alert_check;
      this.last_digest = queryMonitorJSON.last_digest;
      this.query = queryMonitorJSON.query;
      this.title = queryMonitorJSON.title;
      this.updated_at = queryMonitorJSON.updated_at;
    } catch (error) {
      console.error(`Error assigning queryMonitorJSON: ${error}`);
      Sentry.captureException(error);
    }
  }

  getDigestWeekdaysValues(): Array<DayOfWeekValue> {
    if (this.digest_weekdays.length === 0) {
      return [];
    }
    return this.digest_weekdays.map((digestWeekday) => digestWeekday.id);
  }
}